import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { T, useGT } from 'gt-react';
import { ArrowSquareOutIcon, FileIcon } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { PDFViewer } from '@/components/common/pdf-viewer';
import { ExcelDocViewer } from '@/components/xlsx-viewer/ExcelDocViewer';
import useGetInboundEmail from '@/api/core/inbound-emails/getInboundEmail';
import { cn } from '@/lib/utils';
import { attachmentKind, type AttachmentKind } from './attachmentKind';
import { AttachmentFileIcon } from './AttachmentFileIcon';
import { ArchiveMemberPicker } from './ArchiveMemberPicker';
import { viewableAttachment } from './viewableAttachment';
import { useCanReview } from '../849-review/useCanReview';
import { resolveViewerHeight, viewerBoxClass } from './attachmentViewerLayout';
import { FullMessageDialog } from '@/components/email-viewer/full-message-dialog';
import { EmailBody } from '@/components/email-viewer/email-body';
import { inboundEmailToEmailData } from '@/components/email-viewer/utils';
import { ForwardedEmailsSection } from '@/components/ship-and-debit/ForwardedEmailsSection';
import { MailboxCopiesSection } from './MailboxCopiesSection';
import type { LabelingAttachment, LabelingEmailItem } from '@erp/rpc';

/**
 * Largest image we'll fetch before anyone asks to see it.
 *
 * Signature logos and disclaimers — the noise this preview exists to dismiss — are
 * a few KB. 200 KB is roughly six seconds on the ~0.25 Mbps links S&D reviewers
 * work over, which is the most a convenience should ever cost them.
 */
const AUTO_PREVIEW_MAX_BYTES = 200 * 1024;

interface Props {
  item: LabelingEmailItem;
  /**
   * Render the whole message inline instead of the excerpt + "View full message".
   * The review modal's email pane scrolls on its own, so it has the room the
   * Browse slide-over does not.
   */
  fullBody?: boolean;
}

/**
 * The email, reduced to what a labeling decision actually needs.
 *
 * Deliberately NOT the shared email viewer: that renders the whole THREAD when the
 * email has a conversationId, which buries the one message being judged under
 * replies. The thread is worth reading — in the 849 Inbox, where the job is
 * reconciling a claim. Here the job is a snap judgement on one message.
 *
 * Progressive disclosure: a body excerpt and attachment names by default, with the
 * full body and inline attachment previews one click away. Most emails are decidable
 * from the excerpt; the ones that aren't get expanded, and the full body is only
 * fetched when asked for.
 *
 * Sender and subject carry no field labels and no controls of their own — they're
 * context that usually settles the question on its own, not things to annotate.
 */
export function EmailQuickPreview({ item, fullBody = false }: Props) {
  const gt = useGT();
  // Holds an id rather than a boolean: the same viewer renders the parent message
  // and any email forwarded onto it as an attachment.
  const [openMessageId, setOpenMessageId] = useState<string | null>(null);
  const [openAttachmentId, setOpenAttachmentId] = useState<string | null>(null);
  const openAttachment =
    item.attachments.find((a) => a.attachmentId === openAttachmentId) ?? null;

  return (
    <div
      className={cn(
        'space-y-4',
        // The review pane: a fixed-height column in which only the body scrolls,
        // so the attachments stay in view however long the message is.
        fullBody &&
          'lg:flex lg:min-h-0 lg:flex-1 lg:flex-col lg:space-y-0 lg:gap-4',
      )}
    >
      {/* Two sections, because they answer different questions: the body is context
          for "is this an 849 at all", the attachments are where the decision usually
          lives and each one needs its own verdict. */}
      <section
        className={cn(
          'space-y-1.5',
          fullBody && 'lg:flex lg:min-h-0 lg:flex-1 lg:flex-col',
        )}
      >
        <SectionHeading>
          <T context="labeling section heading">Email body</T>
        </SectionHeading>
        {fullBody ? (
          <FullBody inboundEmailId={item.inboundEmailId} />
        ) : (
          <BodyPreview
            item={item}
            onViewFull={() => setOpenMessageId(item.inboundEmailId)}
          />
        )}
      </section>

      {/* Capped only so a long list of attachments cannot squeeze the body to
          nothing; at a normal count it never scrolls. */}
      <div
        className={cn(
          'space-y-4',
          fullBody && 'lg:max-h-[40%] lg:shrink-0 lg:overflow-auto',
        )}
      >
        {item.attachments.length > 0 ? (
          <section className="space-y-1.5">
            <SectionHeading>
              <T context="labeling section heading">Attachments</T>
            </SectionHeading>
            <div className="space-y-1.5">
              {item.attachments.map((a) => (
                <AttachmentRow
                  key={a.attachmentId}
                  attachment={a}
                  onOpen={() => setOpenAttachmentId(a.attachmentId)}
                  inboundEmailId={item.inboundEmailId}
                  gt={gt}
                />
              ))}
            </div>
          </section>
        ) : null}

        <ForwardedEmailsSection emails={item.attachedEmails} />
        <MailboxCopiesSection copies={item.copies} />
      </div>

      <AttachmentDialog
        inboundEmailId={item.inboundEmailId}
        attachment={openAttachment}
        onClose={() => setOpenAttachmentId(null)}
      />

      <FullMessageDialog
        inboundEmailId={openMessageId}
        onClose={() => setOpenMessageId(null)}
      />
    </div>
  );
}

function SectionHeading({ children }: { children: ReactNode }) {
  return (
    <div className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
      {children}
    </div>
  );
}

/**
 * The whole message — HTML when the supplier sent HTML, which is usually where a
 * table of claim lines lives. Shares the attachment previews' query, so opening
 * an attachment after reading the body costs no second fetch.
 */
function FullBody({ inboundEmailId }: { inboundEmailId: string }) {
  // Same query as AttachmentPreview's, split documents included, so the two
  // share one cached fetch.
  const { data: email, isLoading } = useGetInboundEmail({
    inboundEmailId,
    includeSplitDocuments: true,
  });
  if (isLoading) {
    return (
      <div className="flex justify-center py-6">
        <Spinner />
      </div>
    );
  }
  if (!email) {
    return (
      <p className="text-sm text-muted-foreground">
        <T context="labeling full message load error">
          This message could not be loaded. Close and try again.
        </T>
      </p>
    );
  }
  // The only scroller on the email side: a long thread of quoted replies would
  // otherwise push the attachments — usually where the decision is — off screen.
  // Stacked (below lg) the whole modal scrolls, so it gets a fixed cap instead.
  return (
    <div className="max-h-[50vh] overflow-auto rounded-md lg:max-h-none lg:min-h-0 lg:flex-1">
      <EmailBody emailData={inboundEmailToEmailData(email)} />
    </div>
  );
}

/** The excerpt. The real message opens in a dialog — see FullMessageDialog. */
function BodyPreview({
  item,
  onViewFull,
}: {
  item: LabelingEmailItem;
  onViewFull: () => void;
}) {
  if (!item.bodyPreview) {
    return (
      <div className="space-y-1.5">
        <p className="text-sm text-muted-foreground italic">
          <T context="labeling email empty body">
            This email has no body text.
          </T>
        </p>
        <Button
          variant="link"
          size="sm"
          className="h-auto p-0"
          onClick={onViewFull}
        >
          <T context="labeling body disclosure">View full message</T>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      <p className="text-sm leading-relaxed text-muted-foreground">
        {item.bodyPreview}
      </p>
      <Button
        variant="link"
        size="sm"
        className="h-auto p-0"
        onClick={onViewFull}
      >
        <T context="labeling body disclosure">View full message</T>
      </Button>
    </div>
  );
}

/** One attachment: its name, and an inline preview when it is a small image. */
function AttachmentRow({
  attachment,
  onOpen,
  inboundEmailId,
  gt,
}: {
  attachment: LabelingAttachment;
  onOpen: () => void;
  inboundEmailId: string;
  gt: (s: string, o?: { context?: string }) => string;
}) {
  /**
   * Images preview WITHOUT a click.
   *
   * Most image attachments on this mailbox are signature logos and disclaimers —
   * noise the reviewer needs to dismiss, not open. A thumbnail settles that at a
   * glance, where a filename like `image003.png` tells you nothing and costs a click
   * to rule out. (`prefillAttachmentRole` already pre-marks images "Not relevant";
   * the thumbnail is what lets a human confirm that instead of trusting it blindly —
   * and spot the occasional supplier who screenshots their decision.)
   */
  const isImage =
    attachmentKind(attachment.mimeType, attachment.fileName) === 'image';

  /**
   * Auto-preview only SMALL images.
   *
   * `max-h-28` scales what's drawn, not what's fetched: the browser still downloads
   * and decodes the full original. S&D reviewers work on ~0.25 Mbps connections, so
   * silently pulling a multi-megabyte screenshot for every card would stall the
   * queue — the opposite of the "dismiss it at a glance" this exists for.
   *
   * The target case is signature logos and disclaimers, which are a few KB. Anything
   * bigger is plausibly a real screenshot of a decision, and that deserves the
   * deliberate full-size modal anyway. Unknown size counts as too big: guessing
   * cheap and being wrong costs the reviewer their next minute.
   */
  const autoPreview =
    isImage &&
    attachment.sizeBytes != null &&
    attachment.sizeBytes <= AUTO_PREVIEW_MAX_BYTES;

  return (
    <div className="rounded border">
      <div className="flex items-center gap-2 px-3 py-2">
        {/* Opens a full-size modal. An inline expander gave a cramped viewer inside
            an already-narrow card, which is no way to read a claim workbook. */}
        {/* The full name on hover: in a half-width pane most supplier file
            names truncate, and the name is often what tells two workbooks apart. */}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={onOpen}
              className="flex min-w-0 flex-1 items-center gap-1.5 text-left hover:underline"
            >
              <AttachmentFileIcon
                mimeType={attachment.mimeType}
                fileName={attachment.fileName}
              />
              <span className="truncate text-sm">{attachment.fileName}</span>
            </button>
          </TooltipTrigger>
          <TooltipContent className="max-w-md break-all">
            {attachment.fileName}
          </TooltipContent>
        </Tooltip>
      </div>

      {autoPreview ? (
        <button
          type="button"
          onClick={onOpen}
          className="block w-full cursor-zoom-in"
          title={gt('Open this attachment', {
            context: 'labeling attachment open tooltip',
          })}
        >
          <AttachmentPreview
            inboundEmailId={inboundEmailId}
            attachmentId={attachment.attachmentId}
            fileName={attachment.fileName}
            compact
          />
        </button>
      ) : null}
    </div>
  );
}

/**
 * One attachment, full size, in a modal.
 *
 * Excel goes through ExcelDocViewer and PDF through PDFViewer — the same viewers the
 * 849 Inbox uses — because the decision usually lives INSIDE those files, and judging
 * "is this the decision sheet?" means actually reading the claim lines.
 */
export function AttachmentDialog({
  inboundEmailId,
  attachment,
  onClose,
}: {
  inboundEmailId: string;
  attachment: LabelingAttachment | null;
  onClose: () => void;
}) {
  return (
    <Dialog
      open={attachment != null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      {/* Resizable (and much larger by default): the decision lives inside the
          sheet, and a 6xl box on a wide monitor made reviewers scroll a claim
          they could otherwise have read at a glance. `resize` needs a non-visible
          overflow to take effect, hence `overflow-hidden` here and the scroll
          moved onto the body below.

          The `sm:` cap is load-bearing: `DialogContent`'s base ends in
          `sm:max-w-lg`, which an unprefixed `max-w-*` does not override (see
          FullMessageDialog) — without it this rendered at its 520px minimum. */}
      <DialogContent className="flex h-[90vh] max-h-[95vh] min-h-[320px] w-[94vw] min-w-[min(520px,94vw)] max-w-[1700px] resize flex-col gap-0 overflow-hidden p-0 sm:max-w-[1700px]">
        <DialogHeader className="border-b px-5 py-3">
          <DialogTitle className="truncate text-base">
            {attachment?.fileName}
          </DialogTitle>
        </DialogHeader>
        <div className="flex min-h-0 flex-1 flex-col overflow-auto">
          {attachment ? (
            <AttachmentPreview
              inboundEmailId={inboundEmailId}
              attachmentId={attachment.attachmentId}
              fileName={attachment.fileName}
              fill
            />
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Renders the attachment the reviewer opened.
 *
 * The signed URLs live on the full email, so this only fetches once someone actually
 * opens something — the common case (judge from the filename and the sender) costs
 * nothing.
 */
function AttachmentPreview({
  inboundEmailId,
  attachmentId,
  fileName,
  compact = false,
  fill = false,
}: {
  inboundEmailId: string;
  attachmentId: string;
  fileName: string;
  /** Thumbnail height, for an image shown before anyone asked to open it. */
  compact?: boolean;
  /**
   * Take all the height the parent gives instead of the inline 420px cap.
   *
   * Set by the full-size dialog only. Without it the viewer stayed 420px tall
   * inside a 90vh modal — the "the Excel modal is too small" complaint: the
   * modal was big, the sheet inside it wasn't.
   */
  fill?: boolean;
}) {
  // Split documents too — see `viewableAttachment`.
  const { data: email, isLoading } = useGetInboundEmail({
    inboundEmailId,
    includeSplitDocuments: true,
  });

  if (isLoading) {
    return (
      <div className="flex justify-center border-t py-6">
        <Spinner />
      </div>
    );
  }

  const found = viewableAttachment(email, attachmentId);
  if (!found) {
    return (
      <p className="border-t px-3 py-4 text-sm text-muted-foreground">
        <T context="labeling attachment unavailable">
          This attachment could not be loaded.
        </T>
      </p>
    );
  }

  return (
    <div
      className={cn('border-t bg-card', fill && 'flex min-h-0 flex-1 flex-col')}
    >
      <div
        className={compact ? 'hidden' : 'flex shrink-0 justify-end px-3 py-1.5'}
      >
        <a
          href={found.s3Url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary"
        >
          <ArrowSquareOutIcon className="size-3.5" />
          <T context="labeling attachment open">Open</T>
        </a>
      </div>
      <AttachmentBody
        kind={attachmentKind(found.mimeType, fileName)}
        inboundEmailId={inboundEmailId}
        url={found.s3Url}
        fileName={fileName}
        attachmentId={attachmentId}
        compact={compact}
        fill={fill}
      />
    </div>
  );
}

/**
 * The live pixel height of an element.
 *
 * An observer rather than a calculation because the dialog is user-resizable: a
 * height derived once from the viewport goes stale the moment someone drags it.
 *
 * No feedback loop — the observed box is sized by its flex parent and scrolls
 * its own overflow, so the child's height can never feed back into it.
 */
function useMeasuredHeight(): [
  (node: HTMLDivElement | null) => void,
  number | null,
] {
  const [node, setNode] = useState<HTMLDivElement | null>(null);
  const [height, setHeight] = useState<number | null>(null);

  useEffect(() => {
    if (!node) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setHeight(entry.contentRect.height);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);

  return [setNode, height];
}

/**
 * The viewer for one attachment kind.
 *
 * A dispatcher and nothing else: each kind owns its own layout below, and the
 * sizing rules they share live in `attachmentViewerLayout.ts` where they are
 * unit-tested. They were inline here until this function grew past the
 * complexity gate — and the rules are worth testing anyway, since getting them
 * wrong is what left an Excel sheet readable through a 420px window.
 */
function AttachmentBody({
  kind,
  inboundEmailId,
  url,
  fileName,
  attachmentId,
  compact,
  fill = false,
}: {
  kind: AttachmentKind;
  inboundEmailId: string;
  url: string;
  fileName: string;
  attachmentId: string;
  compact: boolean;
  /** See AttachmentPreview's `fill`. */
  fill?: boolean;
}) {
  if (kind === 'image') {
    return (
      <ImageAttachment
        url={url}
        fileName={fileName}
        compact={compact}
        fill={fill}
      />
    );
  }
  if (kind === 'pdf') {
    return <PdfAttachment url={url} fill={fill} />;
  }
  if (kind === 'excel') {
    return (
      <ExcelAttachment
        url={url}
        fileName={fileName}
        attachmentId={attachmentId}
        fill={fill}
      />
    );
  }
  if (kind === 'archive') {
    return (
      <ArchiveAttachment
        inboundEmailId={inboundEmailId}
        attachmentId={attachmentId}
        url={url}
        fileName={fileName}
      />
    );
  }
  return <DownloadAttachment url={url} fileName={fileName} />;
}

/**
 * A zip: its files, to pick the related ones from — for a reviewer. Anyone
 * else gets the file itself, as for any kind the page cannot open.
 */
function ArchiveAttachment({
  inboundEmailId,
  attachmentId,
  url,
  fileName,
}: {
  inboundEmailId: string;
  attachmentId: string;
  url: string;
  fileName: string;
}) {
  const canReview = useCanReview();
  if (!canReview) return <DownloadAttachment url={url} fileName={fileName} />;
  return (
    <ArchiveMemberPicker
      inboundEmailId={inboundEmailId}
      attachmentId={attachmentId}
    />
  );
}

/** A thumbnail in the row; the full image in the dialog. */
function ImageAttachment({
  url,
  fileName,
  compact,
  fill,
}: {
  url: string;
  fileName: string;
  compact: boolean;
  fill: boolean;
}) {
  return (
    <div
      className={
        compact
          ? 'flex justify-center p-2'
          : cn('flex justify-center', viewerBoxClass(fill))
      }
    >
      <img
        src={url}
        alt={fileName}
        className={
          compact
            ? 'max-h-28 w-auto rounded border'
            : 'h-auto max-w-full rounded'
        }
      />
    </div>
  );
}

/**
 * No measured height: `PDFViewer` pages from a fixed width and the box scrolls,
 * so filling the dialog already gives it the room. Only the Excel viewer sizes
 * its grid from a number.
 */
function PdfAttachment({ url, fill }: { url: string; fill: boolean }) {
  return (
    <div className={viewerBoxClass(fill)}>
      <PDFViewer url={url} width={620} pageMode="single" />
    </div>
  );
}

/** The one that has to be told its height — see `resolveViewerHeight`. */
function ExcelAttachment({
  url,
  fileName,
  attachmentId,
  fill,
}: {
  url: string;
  fileName: string;
  attachmentId: string;
  fill: boolean;
}) {
  const [boxRef, boxHeight] = useMeasuredHeight();
  const height = resolveViewerHeight(fill, boxHeight);
  return (
    <div ref={boxRef} className={viewerBoxClass(fill)}>
      <ExcelDocViewer
        url={url}
        fileName={fileName}
        documentId={attachmentId}
        {...(height != null ? { height } : {})}
      />
    </div>
  );
}

/** Anything we can't render — offer the file itself. */
function DownloadAttachment({
  url,
  fileName,
}: {
  url: string;
  fileName: string;
}) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center justify-center gap-2 p-4 text-sm text-muted-foreground hover:text-primary"
    >
      <FileIcon className="size-5" /> {fileName}
    </a>
  );
}
